
import { ref as refDb, onValue, child } from "firebase/database";
import { rtdb } from "~/firebaseInstance";
import { ref } from "vue";
import { createGlobalState } from "@vueuse/core";

export const useRealtimeDatabase = createGlobalState(() => {

  const storeId = ref("_")
  const setStoreId = (_storeId: string) => {
    storeId.value = _storeId
  }



  // General 

  const customerRootRef = () => {
    return refDb(rtdb, `customer/${storeId.value}`)
  }

  const pairedRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/paired`)
  }

  const quotaRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/quota`)
  }


  // Cashier lock (v2) — see app/composables/useCashierLock.ts
  const mainCashierRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/main_cashier`)
  }

  const cashiersRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/cashiers`)
  }

  const cashierEntryRef = (browserId: string) => {
    return refDb(rtdb, `customer/${storeId.value}/cashiers/${browserId}`)
  }


  // Jobs / POS
  const jobchainRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/jobchain`)
  }

  const tablesStateRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/tables_state`)
  }

  const tablesV2Ref = () => {
    return refDb(rtdb, `customer/${storeId.value}/tables_v2`)
  }


   const tablesV2HistoryRef_OLD = () => {
    return refDb(rtdb, `customer/${storeId.value}/tables_v2_history`)
  }


  const tablesRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/tables`)
  }

  const lastReceiptIdRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/last_id_receipt_running`)
  }

  const billNumberRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/items_bills`)
  }

  const billNumberRef_OLD = () => {
    return refDb(rtdb, `customer/${storeId.value}/bills_number`)
  }

  const soldoutRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/soldout`)
  }
  // -- End Jobs

  // Read only all computed tables
  const tablesV3Ref = () => {
    return refDb(rtdb, `customer/${storeId.value}/tables_v3`)
  }



  // Terminal 
  const printPreviewFromTerminalRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/print_preview_from_terminal`)
  }

  const handshakeTerminalRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/terminal_handshake`)
  }
  const terminalJobRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/terminal_job`)
  }

  // Terminal job RESULT — a SEPARATE, additive silo (does NOT touch the legacy
  // terminal_job / terminal_handshake contract). Keyed by the job hash the
  // terminal sent, the cashier publishes the running number it assigned:
  //   terminal_job_result/{hash} = { nickName: "Job #N", jobId }
  // The terminal reads its own hash here to toast the assigned number. Legacy
  // clients that never read this node are unaffected.
  const terminalJobResultRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/terminal_job_result`)
  }

  const secondCashierPrintRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/second_cashier_print`)
  }

  // End Terminal ----


  // Mobile
  const handshakeMobileV1Ref = () => {
    return refDb(rtdb, `customer/${storeId.value}/mobile_handshake`)
  }

  // Void-from-mobile silo — the legacy Java app (VoidItemsActivity) pings the
  // handshake node, then pushes a void request onto the void node. The cashier
  // (pos.mobile-void-v1.ts) answers the handshake and converts each request into
  // a JobAction.VOID jobcard.
  const handshakeVoidFromMobileRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/tables_v2_handshake_void_from_mobile`)
  }
  const voidFromMobileRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/tables_v2_void_from_mobile`)
  }
  // Mobile V2 — same handshake shape as the terminal silo, but a separate set of
  // nodes so a shop can run v1 and v2 phones side by side during the rollout.
  //   1. mobile_v2_handshake/{id} : phone writes 1, cashier flips to 2 (auto-clear)
  //   2. mobile_v2_job/{id}       : phone writes the order JSON, cashier ingests
  //                                 then deletes the node
  //   3. mobile_v2_result/{id}    : cashier publishes { read, jobId, nickName }
  //
  // {id} is the ORDER's uniqueid, not a push key, so a retry lands on the same
  // node and can be recognised as a duplicate instead of becoming a second order.
  const mobileV2HandshakeRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/mobile_v2_handshake`)
  }
  const mobileV2JobRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/mobile_v2_job`)
  }
  const mobileV2ResultRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/mobile_v2_result`)
  }
  // 4. mobile_v2_history/{alias}/{pushKey} : per-table usage history for the
  //    phone's History tab, keyed by the table's REAL name ("T7") instead of the
  //    legacy 1-based numeric id (which renumbers whenever a zone is added or
  //    renamed, so the phone ends up reading another table's history).
  //    Written by cloud.mobile-v2-history.plugin.ts; children are raw objects.
  const mobileV2HistoryRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/mobile_v2_history`)
  }
  // End Mobile V2 ----

  // End Mobile ----


  // Legacy JOB nodes (mobile Java client + legacy web cashier still use these).
  // JOB is a separate Firebase silo from the unified jobchain — the SPA gateway
  // (pos.job-gateway.ts) answers the handshake, ingests jobs/mobile orders, and
  // replies on the same node so the phone shows its Job ID.
  const jobHandshakeRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/jobs/handshake`)
  }
  const jobMobileRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/jobs/mobile`)
  }
  const jobTerminalRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/jobs/terminal`)
  }
  const jobTerminalHandshakeRef = () => {
    return refDb(rtdb, `customer/${storeId.value}/jobs/terminal_handshake`)
  }
  // End Legacy JOB ----

  return {
    handshakeMobileV1Ref,
    mobileV2HandshakeRef,
    mobileV2JobRef,
    mobileV2ResultRef,
    mobileV2HistoryRef,
    handshakeVoidFromMobileRef,
    voidFromMobileRef,
    handshakeTerminalRef,
    terminalJobRef,
    terminalJobResultRef,
    setStoreId,
    customerRootRef,
    jobchainRef,
    tablesStateRef,
    lastReceiptIdRef,
    tablesV2Ref,
    tablesV2HistoryRef_OLD,
    tablesV3Ref,
    tablesRef,
    pairedRef,
    quotaRef,
    soldoutRef,
    billNumberRef,
    billNumberRef_OLD,
    printPreviewFromTerminalRef,
    mainCashierRef,
    cashiersRef,
    cashierEntryRef,
    secondCashierPrintRef,
    jobHandshakeRef,
    jobMobileRef,
    jobTerminalRef,
    jobTerminalHandshakeRef,
  }
})
